/*
 * Helpers for surfacing HAAK-2 sentence-completion (free-text) responses.
 *
 * Used by the examiner "Print Responses" button (print-responses.js) and by the
 * examinee questionnaire to split multi-slot prompts.
 */

// Parse a sentence-completion prompt into per-slot descriptors. A "slot" is a run
// of 3+ underscores; the text BEFORE each run is that slot's label. Matches the
// wici-api parser exactly: returns null for single-response prompts (fewer than 2
// runs) so the caller keeps a single bare-key textarea; otherwise returns one
// { index, label } per run, in document order. Slot answers are keyed
// `<baseKey>#<index>` to line up with the report's sub-lettering (61a, 61b, …).
export const parsePromptSlots = (prompt) => {
  const text = typeof prompt === 'string' ? prompt : '';
  // split() drops the underscore runs and yields (runCount + 1) segments; the
  // trailing segment (text after the last run) is ignored.
  const segments = text.split(/_{3,}/);
  const slotCount = segments.length - 1;
  if (slotCount < 2) return null;
  return Array.from({ length: slotCount }, (_, index) => ({
    index,
    label: segments[index].trim()
  }));
};

// An allowedResponses option (a plain string, or { response|label, score|value }) as
// the questionnaire tracks it locally: the string compared to decide which radio is
// checked. A resumed answer must map to exactly this string to redraw as selected.
export const optionDisplay = (option) =>
  typeof option === 'object'
    ? option.response || option.label || String(option.score ?? option.value)
    : String(option);

// The value posted for an option. The server records a score only for numbers, so
// scored options send their numeric score.
export const optionScore = (option) =>
  typeof option === 'object' ? (option.score !== undefined ? option.score : option.value) : option;

// Sentence-completion items are the ones with a free-text responseType.
export const textItemsOf = (record) =>
  (record?.test?.items || []).filter((item) => item.responseType === 'text');

// Resolve an item's free-text answer: responses (keyed by item id) first, then the
// legacy data[responseKey] fallback, then empty.
export const completionFor = (record, item) => {
  const fallbackKey = item.responseKey || `response_${item._id}`;
  return record?.responses?.[item._id] ?? record?.data?.[fallbackKey] ?? '';
};
